import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { dirTokens } from "../tokens";

/**
 * SkeletonTable — placeholder table shown while the real one loads.
 *
 * Replaces the CircularProgress + "Loading X…" text pattern every
 * Directory list page copies. Preserves layout width by rendering
 * a header + N skeleton rows sized to the columns of the real table.
 *
 * Props:
 *   columns : Array<{ label?: string, width?: number|string, align?: string, mono?: boolean }>
 *   rows    : number of skeleton rows to render (default 5)
 *
 * The header labels are optional so a caller can pass placeholder
 * "" strings when the column titles aren't yet known.
 */
export default function SkeletonTable({ columns, rows = 5 }) {
  const cols = columns && columns.length > 0 ? columns : [{ label: "" }];
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      aria-busy="true"
      aria-live="polite"
      sx={{ borderRadius: dirTokens.radius }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {cols.map((c, i) => (
              <TableCell
                key={i}
                align={c.align || "left"}
                sx={
                  c.mono
                    ? { fontFamily: "monospace", width: c.width }
                    : { width: c.width }
                }
              >
                {c.label || ""}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rows }).map((_, r) => (
            <TableRow key={r} sx={{ height: dirTokens.table.rowHeight }}>
              {cols.map((c, i) => (
                <TableCell key={i} align={c.align || "left"}>
                  <Skeleton
                    variant="text"
                    // Wider skeletons for name columns, narrower for numeric.
                    width={
                      c.align === "right"
                        ? 48
                        : c.mono
                          ? 180
                          : `${60 + ((r + i) % 3) * 10}%`
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
